import type { DetailedHTMLProps, ButtonHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import classnames from 'classnames';

import Loader from 'app/components/Loader';

import ResetedButton from './ResetedButton';
import style from './style.scss';

type ButtonVariation =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'warning'
  | 'error'
  | 'error_empty'
  | 'empty'
  | 'empty_border'
  | 'input'
  | 'cta'
  | 'cancel'
  | 'inline'
  | 'text'
  | 'block';

interface ButtonProps
  extends DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  variation?: ButtonVariation;
  loading?: boolean;
  compact?: boolean;
  chipContent?: string;
}

const variationClassMap: Record<ButtonVariation, string> = {
  primary: style.button_primary,
  secondary: style.button_secondary,
  tertiary: style.button_tertiary,
  warning: style.button_warning,
  error: style.button_error,
  error_empty: style.button_error_empty,
  empty: style.button_empty,
  empty_border: style.empty_border,
  input: style.button_input,
  cta: style.button_cta,
  cancel: style.button_cancel,
  inline: style.button_inline,
  text: style.button_text,
  block: style.button_block
};

const variationLoaderColorMap: { [key: string]: string } = {
  primary: 'white',
  secondary: 'white',
  warning: 'white',
  error: 'white',
  empty: '#3399ff',
  empty_border: '#3399ff'
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variation = 'primary',
      type = 'button',
      children,
      className,
      disabled,
      loading,
      onClick,
      compact,
      chipContent,
      ...props
    },
    ref: any
  ) => {
    const buttonClassName = classnames(style.button, variationClassMap[variation], className, {
      [style.button_disabled]: disabled,
      [style.compact]: compact
    });
    const handleClick = loading ? undefined : onClick;

    return (
      <ResetedButton
        ref={ref}
        className={buttonClassName}
        type={type}
        disabled={disabled}
        onClick={handleClick}
        {...props}
      >
        <>
          {chipContent && <span className={style.chip}>{chipContent}</span>}
          {loading ? <Loader size={4} color={variationLoaderColorMap[variation]} /> : children}
        </>
      </ResetedButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;
export type { ButtonProps, ButtonVariation };
export { variationClassMap };
